import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CheckCircle2, AlertCircle, Camera, Gift } from 'lucide-react';

import { redemptionApi } from './api';
import type { PrizeRedemption } from './api';
import { QrCodeScanner } from './QrCodeScanner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';

/**
 * Caso 03 — Booth operator's redemption page. Three states:
 *   1. Empty input → operator types/scans the consumer's code.
 *   2. Preview (state=WON, not expired) → big "Confirm delivery" button.
 *   3. After redeem → success / error banner.
 *
 * The preview query is keyed by the trimmed code so React Query caches
 * each lookup briefly; the redeem mutation invalidates it so a refresh
 * after delivery shows the new DELIVERED state.
 */
export function RedeemPrizePage() {
  const t = useT();
  useFavoritable(t('redemption.title'));
  const [codeInput, setCodeInput] = useState('');
  const [activeCode, setActiveCode] = useState('');
  const [lastResult, setLastResult] = useState<PrizeRedemption | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  // Caso 11 v2 — camera-based scanner. Auto-fills + looks-up when a QR is
  // detected so the operator never has to type the code. v2.5: `?scan=1`
  // from OperatorStationPage opens the scanner directly on mount.
  const [searchParams, setSearchParams] = useSearchParams();
  const [scannerOpen, setScannerOpen] = useState(
    () => searchParams.get('scan') === '1',
  );
  useEffect(() => {
    // Strip the query param after we've consumed it so a manual close
    // doesn't reopen on every render and a refresh doesn't immediately
    // re-trigger the camera permission prompt.
    if (searchParams.get('scan') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('scan');
      setSearchParams(next, { replace: true });
    }
    // Intentionally empty deps — this is a one-shot at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = useQuery({
    queryKey: ['redemption', 'preview', activeCode],
    queryFn: () => redemptionApi.byCode(activeCode),
    enabled: activeCode.length >= 6,
    retry: false,
  });

  const redeem = useMutation({
    mutationFn: (code: string) => redemptionApi.redeem(code),
    onSuccess: (data) => {
      setLastResult(data);
      setLastError(null);
      setCodeInput('');
      setActiveCode('');
    },
    onError: (err: any) => {
      setLastError(err?.message ?? t('redemption.error.generic'));
    },
  });

  function look() {
    const trimmed = codeInput.trim().toUpperCase();
    if (trimmed.length < 6) {
      setLastError(t('redemption.error.tooShort'));
      return;
    }
    setLastError(null);
    setActiveCode(trimmed);
  }

  function confirm() {
    if (!preview.data) return;
    redeem.mutate(preview.data.redemptionCode);
  }

  return (
    <div className="page space-y-6 max-w-xl">
      <header>
        <h1 className="flex items-center gap-2 text-3xl font-bold">
          {t('redemption.title')}
          <HelpHint section="redeem" />
        </h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('redemption.subtitle')}
        </p>
      </header>

      <Card>
        <CardContent className="space-y-3 pt-4">
          <label className="text-xs font-semibold uppercase text-[var(--color-fg-muted)]">
            {t('redemption.codeLabel')}
          </label>
          <input
            type="text"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && look()}
            placeholder="X4P9-MN2K"
            autoFocus
            className="w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-3 text-2xl font-mono tracking-widest"
          />
          <div className="flex gap-2">
            <Button variant="primary" onClick={look} className="flex-1">
              {t('redemption.lookUp')}
            </Button>
            {/* Caso 11 v2 — scan QR option for booth operators. */}
            <Button
              variant="secondary"
              onClick={() => setScannerOpen(true)}
              aria-label={t('operator.scanner.title')}
              leftIcon={<Camera className="h-4 w-4" />}
            >
              {t('redemption.scan')}
            </Button>
          </div>
          {lastError && (
            <div className="flex items-start gap-2 rounded-md bg-[var(--color-error-soft,rgba(255,80,80,0.1))] p-3 text-sm">
              <AlertCircle className="h-4 w-4 text-[var(--color-error)]" />
              <span>{lastError}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {preview.isFetching && activeCode && (
        <Skeleton className="h-40" />
      )}

      {preview.data && preview.data.state === 'WON' && !preview.isFetching && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5" />
              {t('redemption.preview.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Row label={t('redemption.preview.code')} value={preview.data.redemptionCode} />
            <Row
              label={t('redemption.preview.stockId')}
              value={preview.data.stockId ? `#${preview.data.stockId}` : '—'}
            />
            <Row
              label={t('redemption.preview.station')}
              value={preview.data.xkStationId ? `#${preview.data.xkStationId}` : '—'}
            />
            <Row
              label={t('redemption.preview.won')}
              value={
                preview.data.wonAt
                  ? dayjs(preview.data.wonAt).format('MMM D, HH:mm')
                  : '—'
              }
            />
            {preview.data.expiresAt && (
              <Row
                label={t('redemption.preview.expires')}
                value={dayjs(preview.data.expiresAt).format('MMM D, YYYY')}
              />
            )}
            <Button
              variant="primary"
              onClick={confirm}
              disabled={redeem.isPending}
              className="w-full text-lg py-3"
            >
              {redeem.isPending
                ? t('redemption.preview.confirming')
                : t('redemption.preview.confirm')}
            </Button>
          </CardContent>
        </Card>
      )}

      {preview.data && preview.data.state !== 'WON' && (
        <Card>
          <CardContent className="pt-4">
            <Badge tone="warn">{preview.data.state}</Badge>
            <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
              {t(`redemption.preview.state.${preview.data.state.toLowerCase()}`)}
            </p>
          </CardContent>
        </Card>
      )}

      {lastResult && (
        <Card>
          <CardContent className="flex items-center gap-3 pt-4">
            <CheckCircle2 className="h-6 w-6 text-[var(--color-success,#34D399)]" />
            <div>
              <div className="font-bold">{t('redemption.success.title')}</div>
              <div className="text-xs text-[var(--color-fg-muted)]">
                {t('redemption.success.body', { code: lastResult.redemptionCode })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <QrCodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onCode={(code) => {
          // Reuse the manual flow: stash in input, then trigger preview.
          setScannerOpen(false);
          setCodeInput(code);
          setLastError(null);
          setActiveCode(code);
        }}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between border-b border-[var(--color-border-subtle)] pb-1.5">
      <span className="text-xs uppercase text-[var(--color-fg-muted)]">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}
